import {
  isDev,
  NEXT_PUBLIC_CLIENT_DOMAIN,
  NEXT_PUBLIC_PLATFORM_DOMAIN
} from '@utils/constants';
import { ID } from 'types';
import { OVERVIEW_KEY } from '@components/bid_request/SupplierSurvey';

const PROTOCOL = isDev ? 'http' : 'https';
const BASE_CLIENT_URL = `${PROTOCOL}://${NEXT_PUBLIC_CLIENT_DOMAIN}`;
const BASE_PLATFORM_URL = `${PROTOCOL}://${NEXT_PUBLIC_PLATFORM_DOMAIN}`;

const REFRESH_PATH = '/api/v1/auth/refresh';
const SIGN_IN_PATH = '/api/v1/auth';
const SSO_AUTH_PATH = '/api/v1/auth/sso';
const REFRESH_IGNORE_PATHS = [
  SIGN_IN_PATH,
  REFRESH_PATH,
  SSO_AUTH_PATH
];

const APP_ROUTES = {
  API_DOCS_PATH: '/api-docs',
  CREATE_ACCOUNT: '/create_account',
  DEEPLINK: '/deeplink/:token',
  FORGOT_PASSWORD: '/forgot_password',
  LOGIN_PAGE: '/login',
  PERSONAL_ACCESS_TOKENS: 'personal_access_tokens',
  PLAYGROUND: '/playground',
  RECREATE_DEEPLINK: '/deeplink/:token/recreate',
  RESET_PASSWORD: '/reset_password',
  SCORE_SURVEY: '/surveys/:survey_id/score',
  SIDEKIQ_PATH: '/sidekiq',
  SSO_AUTH_CALLBACK_PATH: '/sso/auth/callback',
  SSO_AUTH_FAILURE_PATH: '/sso/auth/failure',
  SSO_AUTH_PATH: '/api/v1/auth/sso',
  SSO_LOGIN: '/sso_login',
  SSO_LOGIN_PAGE: '/sso_login',
  SUPPLIER_REGISTRATION: '/supplier_registration',
};

const PUBLIC_PAGE_ROUTES = [
  APP_ROUTES.PLAYGROUND,
  APP_ROUTES.SUPPLIER_REGISTRATION,
  APP_ROUTES.SCORE_SURVEY
];

const ROOT_AUTHENTICATED_PATH = '/bid_requests';
const SETTINGS_PATH = '/settings';
const SUPER_ROOT_PATH = '/platform_administration/users';

class Routes {
  static get PLATFORM_DOMAIN () { return NEXT_PUBLIC_PLATFORM_DOMAIN; }

  static get PROTOCOL () { return PROTOCOL; }

  static get BASE_CLIENT_URL () { return BASE_CLIENT_URL; }

  static get BASE_PLATFORM_URL () { return BASE_PLATFORM_URL; }

  static get SUPER_ROOT_PATH () { return SUPER_ROOT_PATH; }

  static get PUBLIC_PAGE_ROUTES () { return PUBLIC_PAGE_ROUTES; }

  static get ROOT_AUTHENTICATED_PATH () { return ROOT_AUTHENTICATED_PATH; }

  static get SETTINGS_PATH () { return SETTINGS_PATH; }

  static SETTINGS_TAB_PATH (tab: string) { return `${SETTINGS_PATH}/${tab}`; }

  static get REFRESH_IGNORE_PATHS () { return REFRESH_IGNORE_PATHS; }

  static get REFRESH_PATH () { return REFRESH_PATH; }

  static get SIGN_IN_PATH () { return SIGN_IN_PATH; }

  static get SSO_AUTH_PATH () { return SSO_AUTH_PATH; }

  static get APP_ROUTES () { return APP_ROUTES; }

  static get SIDEKIQ_URL () {
    return `${BASE_PLATFORM_URL}/sidekiq`;
  }

  static get API_DOCS_URL () {
    return `${BASE_PLATFORM_URL}/api/v1/api-docs`;
  }

  // `relayState` carries the original /oauth/authorize URL when an OAuth connector login started this
  // flow. The API forwards it onto the outgoing SAML AuthnRequest, and the IdP echoes it back — which
  // is the only way the value survives the round trip, since the api host's session cookie is
  // SameSite=Strict and absent on the IdP's cross-site POST.
  static AUTH_SSO_URL (email: string, relayState?: string | null) {
    const url = `${BASE_PLATFORM_URL}/api/v1/auth/sso?email=${email}`;

    return relayState
      ? `${url}&RelayState=${encodeURIComponent(relayState)}`
      : url;
  }

  static get MESSAGES_PATH () {
    return '/messages';
  }

  static REQUEST_MESSAGES_PATH (requestId: ID){
    return `/messages/${requestId}`;
  }

  static get GENERAL_MESSAGES_PATH (){
    return '/messages/general';
  }

  static GENERAL_MESSAGES_PATH_WITH_ID (channelId: ID){
    return `/messages/general/${channelId}`;
  }

  static REQUEST_DASHBOARD_PATH (requestId: ID) {
    return `/bid_requests/${requestId}/dashboard`;
  }

  static REQUEST_DOCUMENTS_PATH (requestId: ID) {
    return `/bid_requests/${requestId}/documents`;
  }

  static REQUEST_EVENT_SCHEDULE_PATH (requestId: ID) {
    return `/bid_requests/${requestId}/schedule`;
  }

  static REQUEST_SURVEY_PATH (requestId: ID, activePage?: string) {
    if (activePage?.length && activePage !== OVERVIEW_KEY) {
      return `/bid_requests/${requestId}/survey?activePage=${activePage}`;
    }
    return `/bid_requests/${requestId}/survey`;
  }

  static REQUEST_EVENT_LINE_ITEMS_PATH (requestId: ID, activePage?: string) {
    if (activePage?.length) {
      return `/bid_requests/${requestId}/line_items?activePage=${activePage}`;
    }
    return `/bid_requests/${requestId}/line_items`;
  }

  static REQUEST_MANAGE_SUPPLIERS_PATH (requestId: ID) {
    return `/bid_requests/${requestId}/suppliers`;
  }

  static REQUEST_AWARD_SCENARIOS_PATH (requestId: ID) {
    return `/bid_requests/${requestId}/award`;
  }

  static REQUEST_AWARD_SCENARIO_EXPORT_PATH (requestId: ID, scenarioId: ID, fileFormat: string) {
    return `/bid_requests/${requestId}/award_scenarios/export?id=${scenarioId}&file_format=${fileFormat}`;
  }

  static REQUEST_AWARD_SCENARIO_EXPORT_WORKSHEET_PATH (requestId: ID, scenarioId: ID, fileFormat: string) {
    return `/bid_requests/${requestId}/award_scenarios/export?id=${scenarioId}&worksheet=true&file_format=${fileFormat}`;
  }

  static REQUEST_AWARD_SCENARIO__PATH (requestId: ID, scenarioId: ID) {
    return `/bid_requests/${requestId}/award/scenarios/${scenarioId}`;
  }

  static REQUEST_AWARD_SCENARIO_FINALIZE_PATH (requestId: ID, scenarioId: ID) {
    return `/bid_requests/${requestId}/award/scenarios/${scenarioId}?finalizeAward=true`;
  }

  static REQUEST_AWARD_SCENARIO_CRITERIA_PATH (requestId: ID, scenarioId: ID) {
    return `/bid_requests/${requestId}/award/scenarios/${scenarioId}/criteria`;
  }

  static QUOTE_SUBMISSION_PATH (requestId: ID) {
    return `/bid_requests/${requestId}/quote_submission`;
  }

  static REQUEST_SUBMISSIONS_PATH (requestId: ID) {
    return `/bid_requests/${requestId}/submissions`;
  }

  static REQUEST_ANALYSIS_PATH (requestId: ID) {
    return `/bid_requests/${requestId}/analysis`;
  }

  static REQUEST_SUPPLIER_QUOTE_SUBMISSION_PATH (
    requestId: ID,
    bidResponseId: ID
  ) {
    return `/bid_requests/${requestId}/submissions/quotes/${bidResponseId}`;
  }

  static get ADOPTION_PATH () {
    return '/reporting/adoption';
  }

  static get ADOPTION_USERS_PATH () {
    return '/reporting/adoption/users';
  }

  static get ADOPTION_EVENTS_PATH () {
    return '/reporting/adoption/events';
  }

  static ADOPTION_EVENT_DETAIL_PATH (eventId: ID) {
    return `/reporting/adoption/events/${eventId}`;
  }
}
export default Routes;
